import { useState, useMemo } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { cn } from '@/utils/cn'
import { useSearchStore } from '@/stores/searchStore'
import { useTranslation } from '@/stores/i18nStore'
import { useAdminFiles, useLoadFile, useFetchUrlPreview } from '@/hooks/useSearch'
import { useAnalytics } from '@/hooks/useAnalytics'
import { FileIcon, GlobeIcon, EditIcon, ChevronLeftIcon, CheckIcon } from '@/components/ui/icons'
import type { SearchMode, MatchType, TranslationKey } from '@/types'

type InputTab = 'files' | 'paste' | 'url'

interface UrlSection {
  id: number
  text: string
  selected: boolean
  hasAmharic: boolean
  amharicRatio: number // percentage of Amharic characters
  amharicWordCount: number // count of Amharic words
}

// Check if text contains Amharic (Ethiopic script: U+1200 to U+137F, U+1380-U+139F, U+2D80-U+2DDF)
function getAmharicRatio(text: string): number {
  const amharicRegex = /[\u1200-\u137F\u1380-\u139F\u2D80-\u2DDF]/g
  const amharicChars = (text.match(amharicRegex) || []).length
  const totalChars = text.replace(/\s/g, '').length
  return totalChars > 0 ? amharicChars / totalChars : 0
}

// Count Amharic words (sequences of Ethiopic characters)
function countAmharicWords(text: string): number {
  const amharicWordRegex = /[\u1200-\u137F\u1380-\u139F\u2D80-\u2DDF]+/g
  const matches = text.match(amharicWordRegex) || []
  return matches.length
}

// Split content into sections and identify Amharic content
function splitIntoSections(content: string): UrlSection[] {
  const paragraphs = content
    .split(/\n\n+/)
    .map(p => p.trim())
    .filter(p => p.length > 20) // Filter very short sections

  return paragraphs.map((text, index) => {
    const amharicRatio = getAmharicRatio(text)
    const amharicWordCount = countAmharicWords(text)
    return {
      id: index,
      text,
      selected: amharicRatio > 0.3, // Auto-select if >30% Amharic
      hasAmharic: amharicRatio > 0.1,
      amharicRatio,
      amharicWordCount
    }
  })
}

// Search modes - exclusive (only one can be selected)
const SEARCH_MODES: { id: SearchMode; labelKey: TranslationKey }[] = [
  { id: 'literal', labelKey: 'literal' },
  { id: 'position', labelKey: 'position' },
  { id: 'form', labelKey: 'form' },
  { id: 'numbers', labelKey: 'numbers' }
]

// Match types - multi-select (multiple can be selected)
const MATCH_TYPES: { id: MatchType; labelKey: TranslationKey }[] = [
  { id: 'exact', labelKey: 'exact' },
  { id: 'prefix', labelKey: 'prefix' },
  { id: 'suffix', labelKey: 'suffix' },
  { id: 'contains', labelKey: 'contains' },
  { id: 'sentences', labelKey: 'sentences' }
]

const CHARS_PER_PAGE = 2000 // Characters per page for text viewer

export function GoogleLikeSearch() {
  const { t } = useTranslation()
  const { trackInputMethodChange, trackFileSelect, trackUrlFetch, trackSearchFilterChange } = useAnalytics()
  const {
    searchString,
    searchOptions,
    setSearchMode,
    toggleMatchType,
    setNumberType,
    togglePositionSkip,
    togglePositionRepeat,
    togglePositionExact,
    togglePositionDerivate,
    currentFileInfo,
    currentText,
    loadPastedText,
    loadUrlContent,
    contentSource,
    showTextViewer,
    setShowTextViewer
  } = useSearchStore()

  const [activeTab, setActiveTab] = useState<InputTab>('files')
  const [pasteText, setPasteText] = useState('')
  const [urlInput, setUrlInput] = useState('')
  const [pasteError, setPasteError] = useState('')
  const [urlError, setUrlError] = useState('')

  // Text viewer pagination
  const [viewerPage, setViewerPage] = useState(0)

  // URL Preview state
  const [urlPreviewUrl, setUrlPreviewUrl] = useState<string>('')
  const [urlPreviewTitle, setUrlPreviewTitle] = useState<string | null>(null)
  const [urlFullContent, setUrlFullContent] = useState<string>('')
  const [urlSections, setUrlSections] = useState<UrlSection[]>([])
  const [showFullText, setShowFullText] = useState(false)
  const isUrlPreviewMode = urlSections.length > 0

  const { data: adminFiles, isLoading: filesLoading } = useAdminFiles()
  const loadFile = useLoadFile()
  const fetchUrlPreview = useFetchUrlPreview()

  const handleFileSelect = (fileId: string, fileName: string) => {
    trackFileSelect(parseInt(fileId), fileName)
    loadFile.mutate(fileId)
  }

  const handlePasteSubmit = () => {
    if (pasteText.trim().length < 10) {
      setPasteError(t('textTooShort'))
      return
    }
    setPasteError('')
    trackInputMethodChange('paste')
    loadPastedText(pasteText.trim())
    setPasteText('')
  }

  const handleUrlFetch = () => {
    try {
      new URL(urlInput)
      setUrlError('')
      fetchUrlPreview.mutate(urlInput, {
        onSuccess: (data) => {
          if (data.success && data.content && data.content.trim().length > 10) {
            const sections = splitIntoSections(data.content)
            if (sections.length === 0) {
              trackUrlFetch(urlInput, false)
              setUrlError(t('noContentFound'))
            } else {
              trackUrlFetch(urlInput, true)
              setUrlSections(sections)
              setUrlPreviewUrl(urlInput)
              setUrlPreviewTitle(data.title || null)
              setUrlFullContent(data.content)
            }
          } else {
            trackUrlFetch(urlInput, false)
            setUrlError(data.error || t('noContentFound'))
          }
        }
      })
    } catch {
      setUrlError(t('invalidUrl'))
    }
  }

  const handleUrlBack = () => {
    setUrlSections([])
    setUrlPreviewUrl('')
    setUrlPreviewTitle(null)
    setUrlFullContent('')
    setShowFullText(false)
  }

  const toggleSection = (id: number) => {
    setUrlSections(sections =>
      sections.map(s => s.id === id ? { ...s, selected: !s.selected } : s)
    )
  }

  const selectAmharicOnly = () => {
    setUrlSections(sections =>
      sections.map(s => ({ ...s, selected: s.hasAmharic }))
    )
  }

  const selectAll = () => {
    setUrlSections(sections => sections.map(s => ({ ...s, selected: true })))
  }

  const handleUseSelectedText = () => {
    const selectedText = urlSections
      .filter(s => s.selected)
      .map(s => s.text)
      .join('\n\n')

    if (selectedText.trim().length < 10) {
      setUrlError(t('textTooShort'))
      return
    }
    trackInputMethodChange('url')
    loadUrlContent(urlPreviewUrl, selectedText)
    handleUrlBack()
    setUrlInput('')
  }

  const selectedCount = useMemo(() =>
    urlSections.filter(s => s.selected).length,
    [urlSections]
  )

  const amharicCount = useMemo(() =>
    urlSections.filter(s => s.hasAmharic).length,
    [urlSections]
  )

  // Text viewer pagination
  const totalPages = useMemo(() =>
    currentText ? Math.ceil(currentText.length / CHARS_PER_PAGE) : 0,
    [currentText]
  )

  const currentPageText = useMemo(() => {
    if (!currentText) return ''
    const start = viewerPage * CHARS_PER_PAGE
    const end = start + CHARS_PER_PAGE
    return currentText.slice(start, end)
  }, [currentText, viewerPage])

  const textWordCount = useMemo(() =>
    currentText ? countAmharicWords(currentText) : 0,
    [currentText]
  )

  const getSourceIcon = () => {
    if (contentSource === 'paste') return EditIcon
    if (contentSource === 'url') return GlobeIcon
    return FileIcon
  }

  const SourceIcon = getSourceIcon()

  return (
    <div>
      {/* Selected Content Bar - Flush top */}
      <AnimatePresence>
        {currentFileInfo && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="flex items-center justify-center gap-3 py-2 bg-zinc-100 dark:bg-zinc-900 border-b border-zinc-200 dark:border-zinc-800 -mx-4 md:-mx-8 -mt-4 md:-mt-8 mb-6"
          >
            <div className="flex items-center gap-2 text-sm">
              <SourceIcon className="w-4 h-4 text-zinc-500" />
              <span className="font-medium text-zinc-700 dark:text-zinc-300">
                {currentFileInfo.filename}
              </span>
              <span className="text-zinc-400 dark:text-zinc-500">
                ({Math.round(currentFileInfo.contentLength / 1024)} KB)
              </span>
              {textWordCount > 0 && (
                <span className="text-emerald-600 dark:text-emerald-400 text-xs">
                  • {textWordCount.toLocaleString()} {t('words')}
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => {
                  if (showTextViewer) {
                    setShowTextViewer(false)
                  } else {
                    setViewerPage(0)
                    setShowTextViewer(true)
                  }
                }}
                className="text-xs text-zinc-500 hover:text-zinc-700 dark:hover:text-zinc-300 transition-colors"
              >
                {showTextViewer ? t('close') : t('view')}
              </button>
              <span className="text-zinc-300 dark:text-zinc-700">|</span>
              <button
                onClick={() => useSearchStore.getState().clearContent()}
                className="text-xs text-zinc-500 hover:text-zinc-700 dark:hover:text-zinc-300 transition-colors"
              >
                {t('change')}
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="max-w-4xl mx-auto">
        {/* Hero Section - Input Methods */}
        <AnimatePresence mode="wait">
          {!currentFileInfo && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="text-center py-16 md:py-24"
            >
              {/* Subtitle - hide in preview mode */}
              {!isUrlPreviewMode && (
                <motion.p
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 }}
                  className="text-lg text-zinc-500 dark:text-zinc-400 mb-8"
                >
                  {t('selectInputMethod')}
                </motion.p>
              )}

              {/* Tab Buttons - hide in preview mode */}
              {!isUrlPreviewMode && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.15 }}
                  className="flex justify-center gap-1 mb-6"
                >
                  {[
                    { id: 'files' as InputTab, label: t('inputFiles'), icon: FileIcon },
                    { id: 'paste' as InputTab, label: t('inputPaste'), icon: EditIcon },
                    { id: 'url' as InputTab, label: t('inputUrl'), icon: GlobeIcon }
                  ].map((tab) => (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={cn(
                        'flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all',
                        activeTab === tab.id
                          ? 'bg-zinc-900 dark:bg-white text-white dark:text-zinc-900'
                          : 'text-zinc-500 hover:text-zinc-700 dark:hover:text-zinc-300 hover:bg-zinc-100 dark:hover:bg-zinc-800'
                      )}
                    >
                      <tab.icon className="w-4 h-4" />
                      {tab.label}
                    </button>
                  ))}
                </motion.div>
              )}

              {/* Tab Content */}
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className={cn(
                  'mx-auto',
                  isUrlPreviewMode ? 'max-w-2xl' : 'max-w-lg'
                )}
              >
                <AnimatePresence mode="wait">
                  {/* Files Tab - Amazon Books Style */}
                  {activeTab === 'files' && !isUrlPreviewMode && (
                    <motion.div
                      key="files"
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: 10 }}
                      className="w-full max-w-2xl"
                    >
                      {filesLoading ? (
                        <div className="grid grid-cols-3 sm:grid-cols-4 gap-4">
                          {[1, 2, 3, 4].map((i) => (
                            <div key={i} className="flex flex-col items-center gap-2">
                              <div className="w-24 h-32 bg-zinc-200 dark:bg-zinc-800 rounded-md animate-pulse" />
                              <div className="h-3 w-16 bg-zinc-200 dark:bg-zinc-800 rounded animate-pulse" />
                            </div>
                          ))}
                        </div>
                      ) : adminFiles && adminFiles.length > 0 ? (
                        <div className="grid grid-cols-3 sm:grid-cols-4 gap-4 justify-items-center">
                          {adminFiles.map((file, index) => {
                            const colors = [
                              { bg: 'from-amber-600 to-amber-800' },
                              { bg: 'from-emerald-600 to-emerald-800' },
                              { bg: 'from-sky-600 to-sky-800' },
                              { bg: 'from-rose-600 to-rose-800' },
                              { bg: 'from-violet-600 to-violet-800' },
                              { bg: 'from-orange-600 to-orange-800' },
                              { bg: 'from-teal-600 to-teal-800' },
                              { bg: 'from-indigo-600 to-indigo-800' },
                            ]
                            const color = colors[index % colors.length]
                            const hasCover = file.coverUrl

                            return (
                              <motion.button
                                key={file.id}
                                onClick={() => handleFileSelect(file.id, file.filename)}
                                disabled={loadFile.isPending}
                                whileHover={{ scale: 1.05, y: -4 }}
                                whileTap={{ scale: 0.98 }}
                                className="flex flex-col items-center gap-2 group disabled:opacity-50 disabled:cursor-not-allowed w-28"
                              >
                                {/* Book Cover */}
                                <div
                                  className={cn(
                                    'relative w-24 h-32 rounded-md shadow-lg overflow-hidden',
                                    'transition-shadow group-hover:shadow-xl',
                                    !hasCover && 'bg-gradient-to-br',
                                    !hasCover && color.bg
                                  )}
                                >
                                  {hasCover ? (
                                    /* Actual cover image */
                                    <img
                                      src={file.coverUrl}
                                      alt={file.filename}
                                      className="w-full h-full object-cover"
                                    />
                                  ) : (
                                    /* Placeholder cover design */
                                    <>
                                      {/* Spine shadow */}
                                      <div className="absolute inset-y-0 left-0 w-2 bg-black/20" />

                                      {/* Simple design - just decorative lines */}
                                      <div className="absolute inset-0 flex flex-col items-center justify-center p-3">
                                        <div className="w-10 h-0.5 bg-white/40" />
                                        <div className="w-6 h-0.5 bg-white/30 mt-2" />
                                        <div className="w-8 h-0.5 bg-white/30 mt-1" />
                                      </div>

                                      {/* Shine effect */}
                                      <div className="absolute inset-0 bg-gradient-to-br from-white/10 via-transparent to-transparent" />
                                    </>
                                  )}

                                  {/* Page edges (right side) */}
                                  <div className="absolute top-1 bottom-1 right-0 w-1 bg-zinc-100 dark:bg-zinc-300 rounded-r-sm" />
                                </div>

                                {/* Book title below */}
                                <div className="text-center w-full">
                                  <p className="text-xs font-medium text-zinc-700 dark:text-zinc-300 line-clamp-2 group-hover:text-zinc-900 dark:group-hover:text-white transition-colors font-ethiopic leading-tight">
                                    {file.filename.replace(/\.[^/.]+$/, '')}
                                  </p>
                                  <p className="text-[10px] text-zinc-400 mt-0.5">
                                    {Math.round(file.size / 1024)} KB
                                  </p>
                                </div>
                              </motion.button>
                            )
                          })}
                        </div>
                      ) : (
                        <div className="text-zinc-400 py-8">{t('noFiles')}</div>
                      )}
                    </motion.div>
                  )}

                  {/* Paste Tab */}
                  {activeTab === 'paste' && !isUrlPreviewMode && (
                    <motion.div
                      key="paste"
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: 10 }}
                      className="space-y-3"
                    >
                      <textarea
                        value={pasteText}
                        onChange={(e) => {
                          setPasteText(e.target.value)
                          setPasteError('')
                        }}
                        placeholder={t('pasteTextHere')}
                        className={cn(
                          'w-full h-48 px-4 py-3 rounded-lg text-sm',
                          'bg-zinc-50 dark:bg-zinc-900 border',
                          pasteError
                            ? 'border-red-300 dark:border-red-700'
                            : 'border-zinc-200 dark:border-zinc-800',
                          'text-zinc-900 dark:text-white placeholder:text-zinc-400',
                          'focus:outline-none focus:ring-2 focus:ring-zinc-300 dark:focus:ring-zinc-600',
                          'resize-none font-ethiopic'
                        )}
                      />
                      {pasteError && (
                        <p className="text-sm text-red-500 text-left">{pasteError}</p>
                      )}
                      <div className="flex justify-between items-center">
                        <span className="text-xs text-zinc-400">
                          {pasteText.length} characters
                        </span>
                        <button
                          onClick={handlePasteSubmit}
                          disabled={pasteText.trim().length < 10}
                          className="px-4 py-2 text-sm font-medium rounded-lg bg-zinc-900 dark:bg-white text-white dark:text-zinc-900 hover:bg-zinc-800 dark:hover:bg-zinc-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {t('useThisText')}
                        </button>
                      </div>
                    </motion.div>
                  )}

                  {/* URL Tab - Input */}
                  {activeTab === 'url' && !isUrlPreviewMode && (
                    <motion.div
                      key="url"
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: 10 }}
                      className="space-y-3"
                    >
                      <div className="flex gap-2">
                        <input
                          type="url"
                          value={urlInput}
                          onChange={(e) => {
                            setUrlInput(e.target.value)
                            setUrlError('')
                          }}
                          placeholder={t('enterUrlHere')}
                          className={cn(
                            'flex-1 px-4 py-3 rounded-lg text-sm',
                            'bg-zinc-50 dark:bg-zinc-900 border',
                            urlError
                              ? 'border-red-300 dark:border-red-700'
                              : 'border-zinc-200 dark:border-zinc-800',
                            'text-zinc-900 dark:text-white placeholder:text-zinc-400',
                            'focus:outline-none focus:ring-2 focus:ring-zinc-300 dark:focus:ring-zinc-600'
                          )}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              handleUrlFetch()
                            }
                          }}
                        />
                        <button
                          onClick={handleUrlFetch}
                          disabled={!urlInput.trim() || fetchUrlPreview.isPending}
                          className="px-4 py-2 text-sm font-medium rounded-lg bg-zinc-900 dark:bg-white text-white dark:text-zinc-900 hover:bg-zinc-800 dark:hover:bg-zinc-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                        >
                          {fetchUrlPreview.isPending ? (
                            <div className="w-4 h-4 border-2 border-white dark:border-zinc-900 border-t-transparent rounded-full animate-spin" />
                          ) : (
                            <GlobeIcon className="w-4 h-4" />
                          )}
                          {t('fetch')}
                        </button>
                      </div>
                      {urlError && (
                        <p className="text-sm text-red-500 text-left">{urlError}</p>
                      )}
                      <p className="text-xs text-zinc-400 text-left">
                        Enter a URL to fetch and extract text content for searching.
                      </p>
                    </motion.div>
                  )}

                  {/* URL Preview - Section selection with Amharic detection */}
                  {isUrlPreviewMode && (
                    <motion.div
                      key="url-preview"
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      className="space-y-3"
                    >
                      {/* Header */}
                      <div className="space-y-1">
                        <div className="flex items-center justify-between">
                          <button
                            onClick={showFullText ? () => setShowFullText(false) : handleUrlBack}
                            className="flex items-center gap-1 text-sm text-zinc-500 hover:text-zinc-700 dark:hover:text-zinc-300"
                          >
                            <ChevronLeftIcon className="w-4 h-4" />
                            {showFullText ? t('backToUrl') : t('backToUrl')}
                          </button>
                          <span className="text-xs text-zinc-400 truncate max-w-[200px]">
                            {urlPreviewUrl}
                          </span>
                        </div>
                        {urlPreviewTitle && (
                          <h3 className="text-sm font-medium text-zinc-900 dark:text-white text-center line-clamp-2 px-4">
                            {urlPreviewTitle}
                          </h3>
                        )}
                      </div>

                      {showFullText ? (
                        <>
                          {/* Full text view */}
                          <div className="border border-zinc-200 dark:border-zinc-800 rounded-lg overflow-hidden max-h-80 overflow-y-auto">
                            <div className="p-4 text-sm text-zinc-700 dark:text-zinc-300 whitespace-pre-wrap font-ethiopic leading-relaxed">
                              {urlFullContent}
                            </div>
                          </div>
                          <div className="flex justify-end">
                            <button
                              onClick={() => setShowFullText(false)}
                              className="px-4 py-2 text-sm font-medium rounded-lg bg-zinc-100 dark:bg-zinc-800 text-zinc-700 dark:text-zinc-300 hover:bg-zinc-200 dark:hover:bg-zinc-700 transition-colors"
                            >
                              {t('backToUrl')}
                            </button>
                          </div>
                        </>
                      ) : (
                        <>
                          {/* Quick filters */}
                          <div className="flex items-center justify-center gap-2">
                            <button
                              onClick={selectAmharicOnly}
                              className="px-3 py-1.5 text-xs font-medium rounded-md bg-zinc-100 dark:bg-zinc-800 text-zinc-700 dark:text-zinc-300 hover:bg-zinc-200 dark:hover:bg-zinc-700 transition-colors"
                            >
                              አማርኛ only ({amharicCount})
                            </button>
                            <button
                              onClick={selectAll}
                              className="px-3 py-1.5 text-xs font-medium rounded-md text-zinc-500 hover:text-zinc-700 dark:hover:text-zinc-300 transition-colors"
                            >
                              {t('selectAll')}
                            </button>
                            <button
                              onClick={() => setShowFullText(true)}
                              className="px-3 py-1.5 text-xs font-medium rounded-md text-zinc-500 hover:text-zinc-700 dark:hover:text-zinc-300 transition-colors"
                            >
                              {t('viewAll')}
                            </button>
                          </div>

                          {/* Sections list */}
                          <div className="border border-zinc-200 dark:border-zinc-800 rounded-lg overflow-hidden max-h-72 overflow-y-auto">
                            {urlSections.map((section, index) => (
                              <button
                                key={section.id}
                                onClick={() => toggleSection(section.id)}
                                className={cn(
                                  'w-full text-left px-3 py-2.5 flex gap-3 transition-colors',
                                  'hover:bg-zinc-50 dark:hover:bg-zinc-900/50',
                                  section.selected && 'bg-zinc-50 dark:bg-zinc-900/30',
                                  index !== urlSections.length - 1 && 'border-b border-zinc-100 dark:border-zinc-800/50'
                                )}
                              >
                                <div className="flex flex-col items-center gap-1 flex-shrink-0">
                                  <div className={cn(
                                    'w-4 h-4 rounded border-2 flex items-center justify-center transition-colors',
                                    section.selected
                                      ? 'bg-zinc-900 dark:bg-white border-zinc-900 dark:border-white'
                                      : 'border-zinc-300 dark:border-zinc-600'
                                  )}>
                                    {section.selected && (
                                      <CheckIcon className="w-2.5 h-2.5 text-white dark:text-zinc-900" />
                                    )}
                                  </div>
                                  {section.amharicWordCount > 0 && (
                                    <span className="text-[9px] font-medium text-emerald-600 dark:text-emerald-400 tabular-nums">
                                      {section.amharicWordCount}
                                    </span>
                                  )}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className={cn(
                                    'text-sm text-left line-clamp-2',
                                    section.hasAmharic
                                      ? 'text-zinc-900 dark:text-white font-ethiopic'
                                      : 'text-zinc-400 dark:text-zinc-500'
                                  )}>
                                    {section.text}
                                  </p>
                                </div>
                              </button>
                            ))}
                          </div>

                          {/* Footer */}
                          <div className="flex items-center justify-between">
                            <span className="text-xs text-zinc-400">
                              {selectedCount} / {urlSections.length} {t('selectedCount')}
                            </span>
                            <button
                              onClick={handleUseSelectedText}
                              disabled={selectedCount === 0}
                              className="px-4 py-2 text-sm font-medium rounded-lg bg-zinc-900 dark:bg-white text-white dark:text-zinc-900 hover:bg-zinc-800 dark:hover:bg-zinc-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {t('useSelectedText')}
                            </button>
                          </div>
                        </>
                      )}
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Search Options - Two rows below content bar (hidden when viewing text) */}
        <AnimatePresence>
          {currentFileInfo && !showTextViewer && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="-mx-4 md:-mx-8"
            >
              {/* Row 1: Search Modes (exclusive - radio button style) */}
              <div className="flex items-center justify-center gap-2 py-2 px-4 bg-zinc-50 dark:bg-zinc-900/50 border-b border-zinc-200 dark:border-zinc-800">
                <span className="text-xs text-zinc-400 mr-2">{t('modes')}:</span>
                {SEARCH_MODES.map((mode) => (
                  <button
                    key={mode.id}
                    onClick={() => {
                      trackSearchFilterChange('searchMode', mode.id)
                      setSearchMode(mode.id)
                    }}
                    className={cn(
                      'px-3 py-1 rounded-md text-xs font-medium transition-all duration-200',
                      searchOptions.searchMode === mode.id
                        ? 'bg-zinc-900 dark:bg-white text-white dark:text-zinc-900'
                        : 'text-zinc-500 hover:text-zinc-700 dark:hover:text-zinc-300'
                    )}
                  >
                    {t(mode.labelKey)}
                  </button>
                ))}
              </div>

              {/* Row 2: Match Types (multi-select checkboxes) */}
              <div className="flex items-center justify-center gap-2 py-2 px-4 bg-zinc-50 dark:bg-zinc-900/50 border-b border-zinc-200 dark:border-zinc-800">
                <span className="text-xs text-zinc-400 mr-2">{t('match')}:</span>
                {MATCH_TYPES.map((matchType) => {
                  const isNumbersMode = searchOptions.searchMode === 'numbers'
                  // In numbers mode, only sentences is available (others disabled)
                  const isDisabled = isNumbersMode && matchType.id !== 'sentences'
                  const isSelected = isNumbersMode
                    ? matchType.id === 'sentences' && searchOptions.matchTypes.includes('sentences')
                    : searchOptions.matchTypes.includes(matchType.id)

                  return (
                    <button
                      key={matchType.id}
                      onClick={() => {
                        if (isDisabled) return
                        trackSearchFilterChange('matchType', matchType.id)
                        toggleMatchType(matchType.id)
                      }}
                      disabled={isDisabled}
                      className={cn(
                        'px-3 py-1 rounded-md text-xs font-medium transition-all duration-200',
                        isDisabled
                          ? 'text-zinc-300 dark:text-zinc-600 cursor-not-allowed'
                          : isSelected
                            ? 'bg-zinc-900 dark:bg-white text-white dark:text-zinc-900'
                            : 'text-zinc-500 hover:text-zinc-700 dark:hover:text-zinc-300'
                      )}
                    >
                      {t(matchType.labelKey)}
                    </button>
                  )
                })}
              </div>

              {/* Row 3: Number Type Selector (when numbers mode enabled) */}
              {searchOptions.searchMode === 'numbers' && (
                <div className="flex items-center justify-center gap-2 py-2 px-4 bg-zinc-50 dark:bg-zinc-900/50 border-b border-zinc-200 dark:border-zinc-800">
                  <span className="text-xs text-zinc-400 mr-2">{t('numberType')}:</span>
                  {(['digits', 'written', 'both'] as const).map((type) => (
                    <button
                      key={type}
                      onClick={() => {
                        trackSearchFilterChange('numberType', type)
                        setNumberType(type)
                      }}
                      className={cn(
                        'px-3 py-1 rounded-md text-xs font-medium transition-all duration-200',
                        searchOptions.numberType === type
                          ? 'bg-zinc-900 dark:bg-white text-white dark:text-zinc-900'
                          : 'text-zinc-500 hover:text-zinc-700 dark:hover:text-zinc-300'
                      )}
                    >
                      {t(type)}
                    </button>
                  ))}
                </div>
              )}

              {/* Row 3: Position Options (when position mode enabled and search string exists) */}
              {searchOptions.searchMode === 'position' && searchString.length > 0 && (
                <PositionOptionsRow
                  searchString={searchString}
                  positionOptions={searchOptions.positionOptions}
                  onToggleSkip={togglePositionSkip}
                  onToggleRepeat={togglePositionRepeat}
                  onToggleExact={togglePositionExact}
                  onToggleDerivate={togglePositionDerivate}
                />
              )}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Inline Text Viewer */}
        <AnimatePresence>
          {showTextViewer && currentFileInfo && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="py-8"
            >
              <div className="max-w-3xl mx-auto">
                {/* Text Content */}
                <div className="border border-zinc-200 dark:border-zinc-800 rounded-lg overflow-hidden">
                  <div className="max-h-[60vh] overflow-y-auto p-6">
                    <p className="text-sm text-zinc-700 dark:text-zinc-300 whitespace-pre-wrap font-ethiopic leading-relaxed">
                      {currentPageText}
                    </p>
                  </div>

                  {/* Pagination Footer */}
                  <div className="flex items-center justify-between px-4 py-3 border-t border-zinc-200 dark:border-zinc-800 bg-zinc-50 dark:bg-zinc-900/50">
                    <button
                      onClick={() => setViewerPage(p => Math.max(0, p - 1))}
                      disabled={viewerPage === 0}
                      className="px-3 py-1.5 text-sm font-medium rounded-md bg-zinc-100 dark:bg-zinc-800 text-zinc-700 dark:text-zinc-300 hover:bg-zinc-200 dark:hover:bg-zinc-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {t('previous')}
                    </button>
                    <span className="text-sm text-zinc-500">
                      {t('page')} {viewerPage + 1} / {totalPages}
                    </span>
                    <button
                      onClick={() => setViewerPage(p => Math.min(totalPages - 1, p + 1))}
                      disabled={viewerPage >= totalPages - 1}
                      className="px-3 py-1.5 text-sm font-medium rounded-md bg-zinc-100 dark:bg-zinc-800 text-zinc-700 dark:text-zinc-300 hover:bg-zinc-200 dark:hover:bg-zinc-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {t('next')}
                    </button>
                  </div>
                </div>

                {/* Close button */}
                <div className="flex justify-center mt-4">
                  <button
                    onClick={() => setShowTextViewer(false)}
                    className="px-4 py-2 text-sm font-medium rounded-lg bg-zinc-100 dark:bg-zinc-800 text-zinc-700 dark:text-zinc-300 hover:bg-zinc-200 dark:hover:bg-zinc-700 transition-colors"
                  >
                    {t('backToUrl')}
                  </button>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  )
}

// Tenshuashi characters - characters that can have derivative sounds
// When derivate is applied, these transform: ለ→የ, ሰ→ሸ, ተ→ቸ, ነ→ኘ, etc.
const TENSHUASHI_CHARS = ['ለ', 'ሉ', 'ሊ', 'ላ', 'ሌ', 'ል', 'ሎ', 'ሏ',
                          'ሰ', 'ሱ', 'ሲ', 'ሳ', 'ሴ', 'ስ', 'ሶ', 'ሷ',
                          'ተ', 'ቱ', 'ቲ', 'ታ', 'ቴ', 'ት', 'ቶ', 'ቷ',
                          'ነ', 'ኑ', 'ኒ', 'ና', 'ኔ', 'ን', 'ኖ', 'ኗ',
                          'ደ', 'ዱ', 'ዲ', 'ዳ', 'ዴ', 'ድ', 'ዶ', 'ዷ',
                          'ጀ', 'ጁ', 'ጂ', 'ጃ', 'ጄ', 'ጅ', 'ጆ', 'ጇ',
                          'ገ', 'ጉ', 'ጊ', 'ጋ', 'ጌ', 'ግ', 'ጎ', 'ጏ',
                          'ጠ', 'ጡ', 'ጢ', 'ጣ', 'ጤ', 'ጥ', 'ጦ', 'ጧ',
                          'ጸ', 'ጹ', 'ጺ', 'ጻ', 'ጼ', 'ጽ', 'ጾ', 'ጿ',
                          'ፈ', 'ፉ', 'ፊ', 'ፋ', 'ፌ', 'ፍ', 'ፎ', 'ፏ']

interface PositionOptionsRowProps {
  searchString: string
  positionOptions?: {
    skip: number[]
    repeat: number[]
    exact: number[]
    derivate: boolean
  }
  onToggleSkip: (index: number) => void
  onToggleRepeat: (index: number) => void
  onToggleExact: (index: number) => void
  onToggleDerivate: () => void
}

function PositionOptionsRow({
  searchString,
  positionOptions,
  onToggleSkip,
  onToggleRepeat,
  onToggleExact,
  onToggleDerivate
}: PositionOptionsRowProps) {
  const { t } = useTranslation()
  const chars = [...searchString]

  // Check if there's a tenshuashi character (for derivate option)
  // Derivate applies to the rightmost tenshuashi character
  const hasTenshuashi = chars.some(char => TENSHUASHI_CHARS.includes(char))

  const skip = positionOptions?.skip || []
  const repeat = positionOptions?.repeat || []
  const exact = positionOptions?.exact || []
  const derivate = positionOptions?.derivate || false

  return (
    <div className="flex items-center justify-center gap-4 py-2 px-4 bg-zinc-50 dark:bg-zinc-900/50 border-b border-zinc-200 dark:border-zinc-800 flex-wrap">
      {/* Skip option - characters that are optional {0,1} */}
      <div className="flex items-center gap-1">
        <span className="text-xs text-zinc-400 mr-1">{t('skip')}:</span>
        <div className="flex gap-0.5">
          {chars.map((char, index) => (
            <button
              key={`skip-${index}`}
              onClick={() => onToggleSkip(index)}
              className={cn(
                'w-6 h-6 rounded text-sm font-ethiopic transition-all duration-200',
                skip.includes(index)
                  ? 'bg-orange-500 text-white'
                  : 'bg-zinc-100 dark:bg-zinc-800 text-zinc-600 dark:text-zinc-400 hover:bg-zinc-200 dark:hover:bg-zinc-700'
              )}
              title={t('skipCharacter')}
            >
              {char}
            </button>
          ))}
        </div>
      </div>

      {/* Repeat option - characters that can repeat {1,2} */}
      <div className="flex items-center gap-1">
        <span className="text-xs text-zinc-400 mr-1">{t('repeat')}:</span>
        <div className="flex gap-0.5">
          {chars.map((char, index) => (
            <button
              key={`repeat-${index}`}
              onClick={() => onToggleRepeat(index)}
              className={cn(
                'w-6 h-6 rounded text-sm font-ethiopic transition-all duration-200',
                repeat.includes(index)
                  ? 'bg-blue-500 text-white'
                  : 'bg-zinc-100 dark:bg-zinc-800 text-zinc-600 dark:text-zinc-400 hover:bg-zinc-200 dark:hover:bg-zinc-700'
              )}
              title={t('repeatCharacter')}
            >
              {char}
            </button>
          ))}
        </div>
      </div>

      {/* Exact option - characters with no family expansion */}
      <div className="flex items-center gap-1">
        <span className="text-xs text-zinc-400 mr-1">{t('exact')}:</span>
        <div className="flex gap-0.5">
          {chars.map((char, index) => (
            <button
              key={`exact-${index}`}
              onClick={() => onToggleExact(index)}
              className={cn(
                'w-6 h-6 rounded text-sm font-ethiopic transition-all duration-200',
                exact.includes(index)
                  ? 'bg-emerald-500 text-white'
                  : 'bg-zinc-100 dark:bg-zinc-800 text-zinc-600 dark:text-zinc-400 hover:bg-zinc-200 dark:hover:bg-zinc-700'
              )}
              title={t('exactCharacter')}
            >
              {char}
            </button>
          ))}
        </div>
      </div>

      {/* Derivate option - disabled if no tenshuashi character exists */}
      <div className="flex items-center gap-1">
        <span className={cn(
          'text-xs mr-1',
          hasTenshuashi ? 'text-zinc-400' : 'text-zinc-300 dark:text-zinc-600'
        )}>{t('derivate')}:</span>
        <button
          onClick={hasTenshuashi ? onToggleDerivate : undefined}
          disabled={!hasTenshuashi}
          className={cn(
            'px-2 py-1 rounded text-sm font-ethiopic transition-all duration-200',
            !hasTenshuashi
              ? 'bg-zinc-100 dark:bg-zinc-800 text-zinc-300 dark:text-zinc-600 cursor-not-allowed opacity-50'
              : derivate
                ? 'bg-violet-500 text-white'
                : 'bg-zinc-100 dark:bg-zinc-800 text-zinc-600 dark:text-zinc-400 hover:bg-zinc-200 dark:hover:bg-zinc-700'
          )}
          title={hasTenshuashi ? t('derivateWord') : 'No tenshuashi character in word'}
        >
          {searchString || '—'}
        </button>
      </div>
    </div>
  )
}
